(() => {
  customElements.whenDefined('x-drawer').then(() => {
    if (!customElements.get('vl-size-drawer')) {
      const raisedPanels = new WeakMap();
      customElements.define('vl-size-drawer', class extends customElements.get('x-drawer') {
        get shouldAppendToBody() { return false; }
        connectedCallback() {
          super.connectedCallback();
          this.layerController?.abort();
          this.releasePanelLayer();
          this.layerGeneration = (this.layerGeneration || 0) + 1;
          this.layerController = new AbortController();
          const signal = this.layerController.signal;
          document.addEventListener('keydown', event => this.cycleDrawerTab(event), { capture: true, signal });
          this.addEventListener('dialog:before-show', event => {
            if (event.target === this && this.isConnected) {
              this.layerGeneration += 1;
              this.raisePanelLayer();
            }
          }, { signal });
          this.addEventListener('dialog:after-hide', event => {
            if (event.target === this && !this.open && this.completedLeaveGeneration === this.layerGeneration) this.releasePanelLayer();
          }, { signal });
          this.closest('.shopify-section')?.addEventListener('shopify:section:unload', () => {
            this.layerGeneration += 1;
            this.releasePanelLayer();
            this.layerController.abort();
          }, { signal });
          if (this.open) this.raisePanelLayer();
        }
        cycleDrawerTab(event) {
          if (!this.open || event.key !== 'Tab' || event.altKey || event.ctrlKey || event.metaKey) return;
          const path = event.composedPath();
          const dialogSelector = 'dialog,[role="dialog"],[role="alertdialog"],[aria-modal="true"]';
          if (!path.includes(this) || path.find(node => node instanceof Element && node.matches(dialogSelector)) !== this) return;
          const eligible = element => {
            if (!(element instanceof HTMLElement) || element.tabIndex < 0 || element.matches(':disabled') || !element.getClientRects().length) return false;
            for (let node = element; node; node = node.parentElement || node.getRootNode().host) {
              if (node.hidden || node.inert || node.getAttribute('aria-hidden') === 'true' || node.getAttribute('aria-disabled') === 'true') return false;
              const style = getComputedStyle(node);
              if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse') return false;
              if (node === this) return true;
              if (node.matches(dialogSelector)) return false;
              if (node instanceof HTMLDetailsElement && !node.open && !node.querySelector(':scope > summary')?.contains(element)) return false;
            }
            return false;
          };
          const candidates = [this.shadowRoot?.querySelector('[part~="close-button"]'), ...this.querySelectorAll('a[href],button,input,select,textarea,summary,[tabindex]')].filter(eligible);
          const controls = candidates.filter(element => {
            if (!element.matches('input[type="radio"]') || !element.name) return true;
            const group = candidates.filter(other => other.matches('input[type="radio"]') && other.name === element.name && other.form === element.form && other.getRootNode() === element.getRootNode());
            return element === (group.find(other => other.checked) || group[0]);
          }).sort((a, b) => (a.tabIndex > 0 ? a.tabIndex : Infinity) - (b.tabIndex > 0 ? b.tabIndex : Infinity));
          const active = path.find(node => controls.includes(node));
          const index = controls.indexOf(active);
          const target = index < 0
            ? (event.shiftKey ? controls.at(-1) : controls[0])
            : controls[(index + (event.shiftKey ? -1 : 1) + controls.length) % controls.length];
          event.preventDefault();
          event.stopImmediatePropagation();
          (target || this).focus({ preventScroll: true });
          if (target) this.revealDrawerControl(target);
        }
        revealDrawerControl(target) {
          const body = this.shadowRoot?.querySelector('[part~="body"]');
          if (!body || !this.contains(target) || !body.clientHeight) return;
          const roots = Array.from(body.querySelectorAll('slot')).flatMap(slot => slot.assignedElements({ flatten: true }));
          const inBody = element => roots.some(root => root === element || root.contains(element));
          if (!inBody(target)) return;
          const label = target.matches('input[type="radio"].sr-only') && Array.from(target.labels || []).find(element => inBody(element) && element.getClientRects().length && getComputedStyle(element).visibility === 'visible');
          const rect = (label || target).getBoundingClientRect();
          const bodyRect = body.getBoundingClientRect();
          const margin = Math.min(4, body.clientHeight / 4);
          const top = bodyRect.top + body.clientTop + margin;
          const bottom = bodyRect.top + body.clientTop + body.clientHeight - margin;
          let delta = 0;
          if (rect.height > bottom - top || rect.top < top) delta = rect.top - top;
          else if (rect.bottom > bottom) delta = rect.bottom - bottom;
          if (delta) body.scrollTop += delta;
        }
        createLeaveAnimationControls() {
          const generation = this.layerGeneration = (this.layerGeneration || 0) + 1;
          const controls = super.createLeaveAnimationControls();
          controls.finished.then(() => {
            if (generation === this.layerGeneration) this.completedLeaveGeneration = generation;
          }, () => {});
          return controls;
        }
        raisePanelLayer() {
          const panel = this.closest('.vl-product__panel');
          if (panel === this.raisedPanel || !panel) return;
          this.releasePanelLayer();
          const drawers = raisedPanels.get(panel) || new Set();
          drawers.add(this);
          raisedPanels.set(panel, drawers);
          panel.setAttribute('data-vl-size-layer', '');
          this.raisedPanel = panel;
        }
        releasePanelLayer() {
          const panel = this.raisedPanel;
          if (!panel) return;
          const drawers = raisedPanels.get(panel);
          drawers?.delete(this);
          if (!drawers?.size) {
            panel.removeAttribute('data-vl-size-layer');
            raisedPanels.delete(panel);
          }
          this.raisedPanel = null;
        }
        disconnectedCallback() {
          this.layerGeneration = (this.layerGeneration || 0) + 1;
          this.layerController?.abort();
          this.releasePanelLayer();
          super.disconnectedCallback?.();
        }
      });
    }
  });

  if (customElements.get('raith-product')) return;

  class RaithProduct extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.frame = null;
      this.classList.add('vl-enhanced');
      this.confirmedOptions = this.selectedOptions();
      this.sizeAddAttempt = null;
      this.confirmedVariantId = undefined;
      if (window.Shopify?.designMode) {
        document.addEventListener('shopify:inspector:activate', () => {
          if (!this.sizeAddAttempt?.submitted) this.finishSizeAdd();
          this.refreshSizePurchase();
          this.queueUpdate();
        }, { signal });
        document.addEventListener('shopify:inspector:deactivate', () => { this.refreshSizePurchase(); this.queueUpdate(); }, { signal });
      }
      this.setPending(false);
      this.addEventListener('variant:change', (event) => {
        if (event.target !== this.productForm) return;
        this.confirmedOptions = this.selectedOptions();
        this.pendingOptions = null;
        this.selectCurrentVariant(event.detail?.variant);
      }, { signal });
      this.addEventListener('product:rerender', () => this.queueUpdate(), { capture: true, signal });
      this.addEventListener('vl:preview-inset', () => this.queueUpdate(), { signal });
      document.addEventListener('theme:loading:end', () => this.refreshSizePurchase(), { signal });
      this.addEventListener('click', (event) => {
        const confirmButton = event.target.closest('[data-vl-size-add]');
        if (confirmButton) {
          event.preventDefault();
          this.confirmSizePurchase(confirmButton);
          return;
        }
        const input = event.target.closest('label')?.control || event.target.closest('input[data-option-position]');
        if (input?.matches('input[data-option-position]')) {
          this.onOptionClick(event, input);
        }
        if (event.target.closest('[data-vl-mobile-purchase]')) {
          if (this.hasAttribute('data-pending') || !this.canInteractWithPreview) return;
          if (!this.hasAttribute('data-selected')) this.purchaseAction?.focus({ preventScroll: true });
          this.purchaseAction?.click();
        }
      }, { capture: true, signal });
      this.addEventListener('change', (event) => {
        if (!event.target.matches('input[data-option-position]')) return;
        this.onOptionChange(event);
      }, { capture: true, signal });
      this.addEventListener('submit', (event) => {
        if (event.target !== this.productForm) return;
        if (this.hasAttribute('data-cart-uncertain') || (this.sizeAddAttempt && (!this.sizeAddAttempt.authorizing || this.sizeAddAttempt.submitted))) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }
        if (this.hasAttribute('data-pending') || (this.querySelector('variant-picker') && !this.hasAttribute('data-selected'))) {
          event.preventDefault();
          event.stopImmediatePropagation();
          if (!this.hasAttribute('data-pending')) this.querySelector('.vl-size-drawer')?.show();
        } else if (this.sizeAddAttempt) {
          this.sizeAddAttempt.submitted = true;
        }
      }, { capture: true, signal });
      this.addEventListener('variant:add', (event) => {
        if (event.target === this.productForm) this.finishSizeAdd();
      }, { signal });
      this.addEventListener('cart:error', (event) => {
        if (event.target !== this.productForm) return;
        this.finishSizeAdd();
      }, { signal });
      this.addEventListener('cart:uncertain', (event) => {
        if (event.target !== this.productForm) return;
        this.finishSizeAdd();
        this.setAttribute('data-cart-uncertain', '');
        this.querySelectorAll('.vl-native-purchase button[type="submit"]').forEach(button => { button.disabled = true; });
        const status = this.querySelector('[data-vl-cart-uncertain]');
        if (status) { status.hidden = false; status.setAttribute('tabindex', '-1'); status.focus(); }
        this.queueUpdate();
      }, { signal });
      this.addEventListener('vl:variant-error', () => {
        this.restoreOptions(this.confirmedOptions);
        this.pendingOptions = null;
        this.setPending(false);
        const error = this.querySelector('.vl-variant-error');
        if (error) {
          error.hidden = false;
          if (this.querySelector('.vl-size-drawer')?.open) error.focus({ preventScroll: true });
        }
      }, { signal });
      this.addEventListener('toggle', () => this.queueUpdate(), { signal });
      document.addEventListener('toggle', () => this.queueUpdate(), { capture: true, signal });
      ['dialog:before-show', 'dialog:after-show', 'dialog:after-hide'].forEach(type => {
        document.addEventListener(type, () => this.queueUpdate(), { capture: true, signal });
      });
      this.addEventListener('shopify:block:select', (event) => {
        if (event.target.matches('[data-block-type="vl-options"]')) this.querySelector('.vl-size-drawer')?.show(false);
      }, { signal });
      this.addEventListener('shopify:block:deselect', (event) => {
        if (event.target.matches('[data-block-type="vl-options"]')) this.querySelector('.vl-size-drawer')?.hide();
      }, { signal });
      window.addEventListener('scroll', () => this.queueUpdate(), { passive: true, signal });
      window.addEventListener('resize', () => this.queueUpdate(), { passive: true, signal });
      window.visualViewport?.addEventListener('resize', () => this.queueUpdate(), { passive: true, signal });
      window.visualViewport?.addEventListener('scroll', () => this.queueUpdate(), { passive: true, signal });
      this.observer?.disconnect();
      this.observer = new ResizeObserver(() => this.queueUpdate());
      this.observer.observe(this);
      const purchaseBar = this.querySelector('.vl-mobile-purchase');
      if (purchaseBar) this.observer.observe(purchaseBar);
      this.placeNativeHero();
      this.centreCurrentColour();
      this.refreshChoice();
      this.setupBackInStock(signal);
      this.queueUpdate();
      customElements.whenDefined('variant-picker').then(() => {
        if (!signal.aborted) this.refreshChoice();
      });
    }
    get productForm() {
      return document.getElementById(this.dataset.formId);
    }
    setupBackInStock(signal) {
      // AMP can attach its automatic button to a hidden cart-upsell form.
      // Its documented popup API lets a separate PDP button select the native
      // variant without moving/rebinding that unrelated provider-owned button.
      if (window.Shopify?.shop !== 'raithclothing.myshopify.com' || !/^[1-9]\d*$/.test(String(window.Shopify?.theme?.id)) || !['main', 'unpublished'].includes(window.Shopify?.theme?.role)) return;
      this.bisIntegrationReady = true;
      let queued = false;
      const schedule = () => {
        if (queued || signal.aborted) return;
        queued = true;
        queueMicrotask(() => {
          queued = false;
          if (!signal.aborted) this.refreshBackInStock();
        });
      };
      const providerNode = node => node.nodeType === 1 && (node.matches('.bis-reset, #BIS_trigger') || node.querySelector('.bis-reset, #BIS_trigger'));
      const observer = new MutationObserver(records => {
        if (records.some(record => record.type === 'attributes'
          ? record.target.id === 'BIS_trigger' || record.target.closest('.bis-reset')
          : record.target.closest?.('.bis-reset') || [...record.addedNodes, ...record.removedNodes].some(providerNode))) schedule();
      });
      observer.observe(document.body, {subtree: true, childList: true, attributes: true, attributeFilter: ['style', 'hidden', 'class', 'aria-hidden', 'data-variant-id']});
      signal.addEventListener('abort', () => {
        observer.disconnect();
        this.bisIntegrationReady = false;
        this.removeAttribute('data-vl-bis-inline');
        this.bisButton?.remove();
        this.bisButton = null;
      }, {once: true});
      this.refreshBackInStock();
    }
    refreshBackInStock() {
      if (!this.bisIntegrationReady) return;
      // Remove our edge suppression while measuring provider eligibility. The
      // marker is restored synchronously, so it cannot create a visibility loop.
      this.removeAttribute('data-vl-bis-inline');
      if (this.bisButton) this.bisButton.hidden = true;
      const visible = element => {
        if (!element?.isConnected || !element.getClientRects().length) return false;
        for (let node = element; node; node = node.parentElement) {
          if (node.hidden || node.inert || node.getAttribute('aria-hidden') === 'true') return false;
          const style = getComputedStyle(node);
          if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse') return false;
        }
        return true;
      };
      const purchase = this.querySelector('.vl-native-purchase');
      if (!purchase || this.hasAttribute('data-pending')) return;
      const nativeTrigger = purchase.querySelector('#BIS_trigger');
      if (nativeTrigger && !nativeTrigger.disabled && visible(nativeTrigger)) {
        this.setAttribute('data-vl-bis-inline', '');
        return;
      }
      const variant = this.querySelector('variant-picker')?.selectedVariant;
      if (variant?.available !== false || !Number.isSafeInteger(Number(variant.id)) || Number(variant.id) <= 0 ||
          this.productForm?.elements.namedItem('id')?.value !== String(variant.id) ||
          typeof window.BIS?.popup?.show !== 'function' || window._BISConfig?.button?.visible === false) return;
      const edgeButtons = document.querySelectorAll('body > .bis-reset > .bis-button.BIS_trigger');
      const edge = edgeButtons.length === 1 ? edgeButtons[0] : null;
      // A visible, correctly bound AMP control is the eligibility source. This
      // preserves app/tag exclusions (including bis-hidden) and app-load failure.
      if (!edge || edge.dataset.variantId !== String(variant.id) || !visible(edge)) return;
      const label = edge.textContent.trim();
      if (!label) return;
      if (!this.bisButton) {
        const button = this.bisButton = document.createElement('button');
        button.type = 'button';
        button.className = 'button';
        button.setAttribute('data-vl-bis-button', '');
        button.addEventListener('click', event => {
          event.preventDefault();
          this.refreshBackInStock();
          if (button.hidden || !visible(button)) return;
          try { window.BIS.popup.show({variantId: Number(button.dataset.variantId)}); }
          catch {
            button.hidden = true;
            this.removeAttribute('data-vl-bis-inline');
          }
        }, {signal: this.controller.signal});
      }
      if (this.bisButton.parentElement !== purchase) purchase.append(this.bisButton);
      this.bisButton.textContent = label;
      this.bisButton.dataset.variantId = String(variant.id);
      this.bisButton.hidden = false;
      if (visible(this.bisButton)) this.setAttribute('data-vl-bis-inline', '');
    }
    get purchaseAction() {
      const sizeAction = this.querySelector('.vl-select-options');
      if (sizeAction && this.querySelector('[data-vl-size-confirm]')) return sizeAction;
      return this.hasAttribute('data-selected')
        ? this.querySelector('.vl-native-purchase button[type="submit"]')
        : sizeAction;
    }
    get editorInspectorActive() {
      return document.documentElement.getAttribute('data-raith-editor-inspector') === 'active' || window.Shopify?.inspectMode === true;
    }
    get canInteractWithPreview() {
      return !window.Shopify?.designMode || !this.editorInspectorActive;
    }
    isOwnOption(input) {
      return input?.matches('input[data-option-position]') &&
        input.closest('variant-picker')?.getAttribute('form-id') === this.dataset.formId &&
        input.closest('raith-product') === this;
    }
    onOptionClick(event, input) {
      if (!this.isOwnOption(input)) return;
      if (this.hasAttribute('data-pending') || this.sizeAddAttempt) {
        event.preventDefault();
        event.stopPropagation();
      } else if (input.checked && JSON.stringify(this.selectedOptions()) === JSON.stringify(this.confirmedOptions)) {
        this.selectCurrentVariant();
      }
    }
    onOptionChange(event) {
      if (!this.isOwnOption(event.target)) return;
      if (this.hasAttribute('data-pending') || this.sizeAddAttempt) {
        this.restoreOptions(this.pendingOptions || this.confirmedOptions);
        event.stopPropagation();
        return;
      }
      this.pendingOptions = this.selectedOptions();
      this.setPending(true);
    }
    selectedPurchase() {
      const form = this.productForm;
      const picker = this.querySelector('variant-picker');
      const variant = picker?.selectedVariant;
      const button = this.querySelector('.vl-native-purchase button[type="submit"]');
      const confirmed = this.hasAttribute('data-selected') && variant?.id &&
        String(this.confirmedVariantId) === String(variant.id) &&
        JSON.stringify(this.selectedOptions()) === JSON.stringify(this.confirmedOptions) &&
        form?.elements.namedItem('id')?.value === String(variant.id) && button?.form === form;
      return { form, picker, variant, button, confirmed };
    }
    refreshSizePurchase() {
      const drawer = this.querySelector('[data-vl-size-confirm]');
      const control = drawer?.querySelector('[data-vl-size-add]');
      if (!control) return;
      const picker = this.querySelector('variant-picker');
      if (this.confirmedVariantId === undefined && this.hasAttribute('data-selected') && picker?.selectedVariant?.id) {
        this.confirmedVariantId = picker.selectedVariant.id;
      }
      const { variant, button, confirmed } = this.selectedPurchase();
      const pending = this.hasAttribute('data-pending') || Boolean(this.sizeAddAttempt) || button?.getAttribute('aria-busy') === 'true';
      const selected = this.hasAttribute('data-selected');
      const soldOut = confirmed && variant?.available === false;
      control.disabled = pending || !this.canInteractWithPreview || this.hasAttribute('data-cart-uncertain') || !confirmed || variant?.available !== true || !button || button.disabled;
      if (window.Shopify?.designMode) {
        const blocked = [];
        if (pending) blocked.push('pending');
        if (!this.canInteractWithPreview) blocked.push('inspector-active');
        if (this.hasAttribute('data-cart-uncertain')) blocked.push('cart-uncertain');
        if (!confirmed) blocked.push('unconfirmed');
        if (variant?.available !== true) blocked.push('variant-unavailable');
        if (!button) blocked.push('native-button-missing');
        else if (button.disabled) blocked.push('native-button-disabled');
        control.dataset.vlSizeDisabledReason = blocked.join(' ') || 'none';
      } else {
        control.removeAttribute('data-vl-size-disabled-reason');
      }
      control.toggleAttribute('aria-busy', pending);
      if (pending) control.setAttribute('aria-busy', 'true');
      control.textContent = !selected ? control.dataset.selectLabel : soldOut ? control.dataset.soldOutLabel : !confirmed ? control.dataset.unavailableLabel : control.dataset.addLabel;
      const selection = drawer.querySelector('[data-vl-size-selection]');
      if (selection) selection.textContent = selected && variant?.id ? variant.title : control.dataset.selectLabel;
      const price = drawer.querySelector('[data-vl-size-price]');
      const source = this.querySelector('template[data-vl-size-price-source]') || this.querySelector('.vl-buybox__blocks [data-block-type="price"] price-list');
      const priceHtml = source instanceof HTMLTemplateElement ? source.innerHTML : source?.outerHTML;
      if (price && priceHtml !== undefined && price.innerHTML !== priceHtml) price.innerHTML = priceHtml;
    }
    confirmSizePurchase(control) {
      if (control.closest('[data-vl-size-confirm]')?.closest('raith-product') !== this || control.disabled) return;
      const { variant, confirmed } = this.selectedPurchase();
      if (!confirmed) return;
      this.addSelectedSize(variant, this.selectedOptions());
    }
    async addSelectedSize(variant, options) {
      if (this.sizeAddAttempt || this.hasAttribute('data-cart-uncertain') || variant?.available !== true) return;
      const form = this.productForm;
      const ready = () => {
        const purchase = this.selectedPurchase();
        return this.canInteractWithPreview && this.isConnected && this.productForm === form && form?.isConnected && !this.hasAttribute('data-pending') &&
          JSON.stringify(this.selectedOptions()) === JSON.stringify(options) && purchase.confirmed &&
          purchase.variant?.available === true && String(purchase.variant.id) === String(variant.id) &&
          !purchase.button.disabled && purchase.button.getAttribute('aria-busy') !== 'true' && purchase.button;
      };
      if (!ready()) return;
      const attempt = this.sizeAddAttempt = { submitted: false, authorizing: false };
      this.querySelector('variant-picker').inert = true;
      this.refreshSizePurchase();
      try {
        // Close only after an explicit purchase, before the native cart opens.
        const drawer = this.querySelector('[data-vl-size-confirm]');
        if (drawer?.open) await drawer.hide();
        if (this.sizeAddAttempt !== attempt) return;
        const button = ready();
        if (!button) { this.finishSizeAdd(); return; }
        attempt.authorizing = true;
        form.requestSubmit(button);
        attempt.authorizing = false;
        if (!attempt.submitted || button.getAttribute('aria-busy') !== 'true') this.finishSizeAdd();
      } catch {
        this.finishSizeAdd();
      }
    }
    finishSizeAdd() {
      this.sizeAddAttempt = null;
      const picker = this.querySelector('variant-picker');
      if (picker) picker.inert = this.hasAttribute('data-pending');
      this.refreshSizePurchase();
      this.queueUpdate();
    }
    selectedOptions() {
      return Array.from(this.querySelectorAll('variant-picker input[data-option-position]:checked'), input => input.value);
    }
    restoreOptions(values = []) {
      this.querySelectorAll('variant-picker input[data-option-position]').forEach(input => {
        input.checked = input.value === values[Number(input.dataset.optionPosition) - 1];
      });
    }
    queueUpdate() {
      if (this.frame === null && this.isConnected) this.frame = requestAnimationFrame(() => this.update());
    }
    placeNativeHero() {
      const media = this.querySelector('[data-vl-responsive-native-hero]');
      if (!media) return;
      const position = window.innerWidth <= 900 ? 'continuation' : 'hero';
      const target = this.querySelector(`[data-vl-native-hero-location="${position}"]`);
      if (target && media.parentElement !== target) {
        media.querySelectorAll('video-media,model-media,video').forEach(player => player.pause?.());
        target.append(media);
      }
    }
    centreCurrentColour() {
      const strip = this.querySelector('.vl-colours');
      const current = strip?.querySelector('[aria-current]');
      if (current) strip.scrollLeft += current.getBoundingClientRect().left - strip.getBoundingClientRect().left - (strip.clientWidth - current.offsetWidth) / 2;
    }
    update() {
      this.frame = null;
      this.placeNativeHero();
      const dialogOpen = Boolean(document.querySelector('[open][role="dialog"],dialog[open],[aria-modal="true"][open]'));
      const headerSolid = window.scrollY > 20 || dialogOpen;
      const row = this.querySelector('[data-vl-purchase]');
      const stop = this.querySelector('[data-vl-purchase-stop]');
      const bar = this.querySelector('.vl-mobile-purchase');
      if (!bar) { this.toggleAttribute('data-header-solid', headerSolid); return; }
      const title = bar.querySelector('[data-vl-mobile-title]');
      const nextTitle = title && (this.querySelector('.vl-product__title')?.textContent.trim() || title.textContent);
      const subtitle = bar.querySelector('[data-vl-mobile-subtitle]');
      const nextSubtitle = this.querySelector('.vl-product__colour')?.textContent.trim() || '';
      const price = bar.querySelector('[data-vl-mobile-price]');
      const sourcePrice = this.querySelector('template[data-vl-size-price-source]')?.content.querySelector('price-list') || this.querySelector('.vl-buybox__block--price price-list');
      const nextPrice = sourcePrice?.outerHTML || '';
      const source = this.purchaseAction;
      const immediate = this.dataset.stickyAppearance === 'immediate';
      const viewport = window.visualViewport;
      const viewportTop = viewport?.offsetTop || 0;
      const viewportBottom = Math.min(window.innerHeight, viewportTop + (viewport?.height || window.innerHeight));
      const previewInset = Math.max(0, parseFloat(this.style.getPropertyValue('--raith-preview-bottom-inset')) || 0);
      const measuredHeight = immediate && !bar.hidden ? bar.getBoundingClientRect().height : 0;
      const barHeight = measuredHeight || this.mobilePurchaseHeight || 140;
      const eligible = row && stop && source && !dialogOpen && this.dataset.stickyEnabled === 'true' && window.innerWidth <= 900;
      let hidden = true;
      if (eligible) {
        const rowBounds = row.getBoundingClientRect();
        const stopBounds = stop.getBoundingClientRect();
        const info = this.querySelector('[data-vl-purchase-info], .vl-buybox__block--title') || row;
        const beforeInfo = immediate && info.getBoundingClientRect().top > viewportBottom - previewInset - barHeight;
        hidden = !(beforeInfo || rowBounds.bottom < viewportTop) || stopBounds.top <= viewportBottom;
      }
      const button = bar.querySelector('button');
      const nextLabel = source?.textContent.trim();
      const busy = this.hasAttribute('data-pending') || Boolean(this.sizeAddAttempt) || source?.getAttribute('aria-busy') === 'true';
      const disabled = Boolean(source?.disabled || busy || this.hasAttribute('data-cart-uncertain') || !this.canInteractWithPreview);
      // Retain the measured height during the inline handover, so hiding the
      // bar does not resize the lead photograph and reverse the boundary.
      if (measuredHeight > 0 && this.mobilePurchaseHeight !== measuredHeight) {
        this.mobilePurchaseHeight = measuredHeight;
        this.style.setProperty('--vl-mobile-purchase-height', `${measuredHeight}px`);
      }
      this.toggleAttribute('data-header-solid', headerSolid);
      if (title && title.textContent !== nextTitle) title.textContent = nextTitle;
      if (subtitle) {
        if (subtitle.textContent !== nextSubtitle) subtitle.textContent = nextSubtitle;
        if (subtitle.hidden !== !nextSubtitle) subtitle.hidden = !nextSubtitle;
      }
      if (price && price.innerHTML !== nextPrice) {
        price.replaceChildren(...(sourcePrice ? [sourcePrice.cloneNode(true)] : []));
      }
      if (bar.hidden !== hidden) bar.hidden = hidden;
      if (source && button) {
        if (button.textContent !== nextLabel) button.textContent = nextLabel;
        if (button.disabled !== disabled) button.disabled = disabled;
        if (button.getAttribute('aria-busy') !== (busy ? 'true' : null)) {
          if (busy) button.setAttribute('aria-busy', 'true');
          else button.removeAttribute('aria-busy');
        }
      }
    }
    setPending(pending) {
      this.toggleAttribute('data-pending', pending);
      const picker = this.querySelector('variant-picker');
      if (picker) picker.inert = pending;
      this.querySelector('.vl-size-drawer')?.setAttribute('aria-busy', String(pending));
      const purchase = this.querySelector('.vl-native-purchase');
      if (purchase) {
        purchase.inert = pending;
        purchase.setAttribute('aria-busy', String(pending));
      }
      const error = this.querySelector('.vl-variant-error');
      if (error && pending) error.hidden = true;
      this.querySelectorAll('.vl-native-purchase button[type="submit"]').forEach(button => {
        if (pending) {
          if (!('vlPreviouslyDisabled' in button.dataset)) button.dataset.vlPreviouslyDisabled = String(button.disabled);
          button.disabled = true;
        } else if ('vlPreviouslyDisabled' in button.dataset) {
          button.disabled = button.dataset.vlPreviouslyDisabled === 'true';
          delete button.dataset.vlPreviouslyDisabled;
        }
      });
      this.refreshStockStatus();
      this.refreshBackInStock();
      this.refreshSizePurchase();
      this.queueUpdate();
    }
    refreshChoice() {
      const variant = this.querySelector('variant-picker')?.selectedVariant;
      this.querySelectorAll('[data-vl-selected-option]').forEach(label => {
        label.hidden = !this.hasAttribute('data-selected') || !variant?.id;
        label.textContent = variant?.title || '';
      });
      this.refreshStockStatus();
      this.refreshBackInStock();
      this.refreshSizePurchase();
    }
    refreshStockStatus() {
      const picker = this.querySelector('variant-picker');
      const variant = picker?.selectedVariant;
      const form = this.productForm;
      const purchase = this.querySelector('.vl-native-purchase');
      let stockStatus = this.querySelector('[data-vl-stock-status]');
      const button = purchase?.querySelector('button[type="submit"]');
      const soldOut = this.hasAttribute('data-selected') && !this.hasAttribute('data-pending') && variant?.available === false &&
        Number.isSafeInteger(Number(variant.id)) && Number(variant.id) > 0 &&
        form?.isConnected && form.closest('raith-product') === this && button?.form === form &&
        picker.getAttribute('form-id') === this.dataset.formId && form.elements.namedItem('id')?.value === String(variant.id) &&
        (this.confirmedVariantId === undefined || String(this.confirmedVariantId) === String(variant.id)) &&
        JSON.stringify(this.selectedOptions()) === JSON.stringify(this.confirmedOptions);
      const nativeLabel = button?.textContent.trim();
      if (soldOut && nativeLabel) {
        if (!stockStatus) {
          stockStatus = document.createElement('p');
          stockStatus.dataset.vlStockStatus = '';
          stockStatus.className = 'vl-stock-status';
          stockStatus.setAttribute('role', 'status');
          stockStatus.setAttribute('aria-atomic', 'true');
          const label = document.createElement('strong');
          label.dataset.vlStockLabel = '';
          const option = document.createElement('span');
          option.dataset.vlStockOption = '';
          stockStatus.append(label, ' ', option);
        }
        // Keep sold-out choices selectable for the native notification app.
        // Display the native stock wording separately from the exact option name.
        if (stockStatus.nextElementSibling !== purchase) purchase.before(stockStatus);
        const label = stockStatus.querySelector('[data-vl-stock-label]');
        const option = stockStatus.querySelector('[data-vl-stock-option]');
        if (label.textContent !== nativeLabel) label.textContent = nativeLabel;
        if (option.textContent !== variant.title) option.textContent = variant.title || '';
        stockStatus.hidden = false;
      } else if (stockStatus) {
        stockStatus.hidden = true;
        stockStatus.querySelectorAll('[data-vl-stock-label], [data-vl-stock-option]').forEach(label => {
          if (label.textContent) label.textContent = '';
        });
      }
    }
    selectCurrentVariant(confirmedVariant = this.querySelector('variant-picker')?.selectedVariant) {
      this.confirmedVariantId = confirmedVariant?.id ?? null;
      const variant = this.querySelector('variant-picker')?.selectedVariant;
      this.setPending(false);
      const error = this.querySelector('.vl-variant-error');
      if (error) error.hidden = true;
      this.setAttribute('data-selected', '');
      this.refreshChoice();
      if (this.hasAttribute('data-cart-uncertain')) this.querySelectorAll('.vl-native-purchase button[type="submit"]').forEach(button => { button.disabled = true; });
      const drawer = this.querySelector('.vl-size-drawer');
      const drawerClosing = drawer?.open && !drawer.hasAttribute('data-vl-size-confirm') && this.canInteractWithPreview ? drawer.hide() : undefined;
      this.placeNativeHero();
      const featured = variant?.featured_media;
      const source = featured?.preview_image?.src;
      const imageKey = value => {
        if (!value) return '';
        try {
          const url = new URL(/^(products|files)\//.test(value) ? '/' + value : value, location.href);
          return url.pathname.replace(/^\/s\/files\/(?:\d+\/)+/, '/').replace(/^\/cdn\/shop\//, '/');
        } catch { return value; }
      };
      const media = featured && Array.from(this.querySelectorAll('[data-media-id],[data-vl-image-src]')).find(item => {
        if (!item.getClientRects().length) return false;
        if (featured.media_type && featured.media_type !== 'image') return item.dataset.mediaId === String(featured.id);
        const shownSource = window.innerWidth <= 900 ? item.dataset.vlMobileImageSrc || item.dataset.vlImageSrc : item.dataset.vlImageSrc;
        const shownId = window.innerWidth <= 900 ? item.dataset.vlMobileMediaId || item.dataset.mediaId : item.dataset.mediaId;
        if (shownId === String(featured.id)) return true;
        // Responsive heroes may share a native ID while showing a different mobile image.
        if (shownSource && source) return imageKey(shownSource) === imageKey(source);
        return false;
      });
      if (media) media.scrollIntoView({ block: 'nearest', behavior: 'auto' });
      this.queueUpdate();
      return drawerClosing;
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.finishSizeAdd();
      this.observer?.disconnect();
      if (this.frame !== null) cancelAnimationFrame(this.frame);
      this.frame = null;
      if (this.hasAttribute('data-pending')) this.restoreOptions(this.confirmedOptions);
      this.setPending(false);
      this.querySelector('.vl-size-drawer')?.hide(false);
    }
  }
  customElements.define('raith-product', RaithProduct);
})();

